/**
 * A repository's `apiboo.yaml` names ONE workspace — its id and its name, as
 * written by the device that connected first. Every other device has its own
 * local id (and possibly its own name: "Clone a repository" lets you name the
 * clone) for the same workspace, and used to write those instead — so after
 * "Keep all remote" on a second device the file changed straight back ("1
 * change waiting"), and the devices rewrote each other's id on every sync,
 * forever (Windows Sandbox, card 06010f63, 03.10.2026). A clone named
 * differently would have renamed the workspace for everyone the same way.
 *
 * Like `git clone <url> <folder>`: the local name is local. A Pull never
 * applies the repository's name either, so the file's id and name are the
 * repository's identity and are kept; only a new file (a first connect, an
 * empty repository) takes the device's. Lines are swapped as text so the
 * writer's own formatting stays byte-identical.
 */
import { keepProjectBlock } from '../../scope/projectManifestText.js';

const KEYS = ['id', 'name'];
const lineOf = (key) => new RegExp(`^${key}:[^\\S\\r\\n]*.*$`, 'm');
const hasValue = (key, line) => new RegExp(`^${key}:[^\\S\\r\\n]*\\S`).test(line);

/**
 * @param {string|null} existing - the file on disk, or null when there is none
 * @param {string} generated - what this device would write
 * @returns {string} generated, carrying the existing file's id and name lines
 */
export function keepRepoIdentity(existing, generated) {
  if (typeof existing !== 'string' || typeof generated !== 'string') return generated;
  // Workspace manifests only. A boards & documents repository's manifest
  // (`apiboo: scope`) has no device id, and its name follows the team on
  // purpose (scopeRepo.refreshScopeMirror passes it when it changes).
  if (/^apiboo:[^\S\r\n]*scope[^\S\r\n]*$/m.test(generated)) return generated;
  let out = generated;
  for (const key of KEYS) {
    const kept = existing.match(lineOf(key));
    if (!kept || !hasValue(key, kept[0]) || !lineOf(key).test(out)) continue;
    out = out.replace(lineOf(key), kept[0].replace(/\r$/, ''));
  }
  // The project block (card 2.2) follows the same rule when this device has none.
  return keepProjectBlock(existing, out);
}
